import {Drawer, Tag, Tooltip, message} from "antd";
import {useCallback, useEffect, useState} from "react";
import type {MelangeDistributionResult, Raid} from "@/raids/rest/RaidSchema.ts";
import {useMelangePreview} from "@/raids/rest/RaidQueries.ts";
import {MelangePreviewInput} from "@/raids/raidDetail/MelangePreviewInput.tsx";
import {MelangePreviewResult} from "@/raids/raidDetail/MelangePreviewResult.tsx";
type Props = {
  raid: Raid | null;
  disabled?: boolean;
};

export function SpiceRefining({raid, disabled = false}: Props) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<MelangeDistributionResult | null>(null);
  const [msgApi, contextHolder] = message.useMessage();

  const melangePreview = useMelangePreview({
    onSuccess: (data) => {
      setResult(data);
      void msgApi.success("Verteilung berechnet");
    },
    onError: (_status, data) => {
      let text = "Berechnung fehlgeschlagen";
      if (typeof data === "string") text = data;
      else if (data && typeof data === "object" && "message" in data) {
        const m = (data as { message?: unknown }).message;
        if (typeof m === "string") text = m;
      }
      void msgApi.error(text);
    },
  });

  useEffect(() => {
    if (disabled || raid?.status === "CREATED") {
      setOpen(false);
    }
  }, [disabled, raid?.status]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setResult(null);
  }, []);

  const handleCalculate = useCallback(
    (
      totalSpiceSand: number,
      landsraatBonus: boolean,
      bonusConversionCount: number,
      withoutTime: boolean,
    ) => {
      if (!raid) return;
      setResult(null);
      melangePreview.mutate({
        raidId: raid.id,
        payload: {
          raid,
          totalSpiceSand,
          landsraatBonus,
          bonusConversionCount,
          withoutTime,
        },
      });
    },
    [melangePreview, raid],
  );

  const notStarted = Boolean(raid && raid.status === "CREATED");
  const blocked = disabled || !raid || notStarted;

  const tag = (
      <Tag
          data-tour="spice-calculator"
          className={
            blocked ? "cursor-not-allowed opacity-50 pointer-events-none" : "cursor-pointer"
          }
          onClick={() => {
            if (blocked) return;
            setOpen(true);
          }}
      >
        Spice Rechner
      </Tag>
  );

  return (
    <>
      {contextHolder}
      {notStarted ? (
          <Tooltip title="Erst verfügbar, wenn der Raid gestartet wurde.">
            <span className="inline-block">{tag}</span>
          </Tooltip>
      ) : (
          tag
      )}
      <Drawer
        title="Spice Rechner"
        placement="right"
        width={480}
        open={open}
        onClose={handleClose}
        destroyOnClose
      >
        {raid && (
          <div className="flex flex-col gap-6">
            <MelangePreviewInput
              raid={raid}
              drawerOpen={open}
              loading={melangePreview.isPending}
              onCalculate={handleCalculate}
            />
            {result && <MelangePreviewResult result={result} />}
          </div>
        )}
      </Drawer>
    </>
  );
}
